import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import Button from '../../ui/Button';
import DataTable from '../../ui/DataTable/DataTable';
import { CellId, CellMuted, CellPlate, CellStatus, CellText } from '../../ui/DataTable/cells';
import { toast } from '../../ui/feedback';
import DrillDownModal from '../KPIModule/components/DrillDownModal';
import { finesService, errorMessage } from '../../services/finesService';
import useOverview from './useOverview';
import { usePermissions } from '../../hooks/usePermissions';
import {
    attentionOf, branchRows, contractStatusOf, FINES_LINK, fmtN, openByBranchText, pct, UNPAID_FINES_SCOPE,
} from './overview.logic';
import { dubaiTime } from '../../utils/dubaiTime';
import CustomerProfileModal from '../../components/CustomerProfileModal';

// every figure opens the records behind it (owner, 3 Oct 2026: "الارقام مش اكتف لما ادوس عليها المفروض اشوف بياناتها")
// - the engine's /api/overview/list, the same rule as the figure
const LISTS = {
    open: 'Contracts open now', opened: 'Contracts opened in the period', returned: 'Contracts returned in the period',
    overdue: 'Contracts past their return date', moved: 'Contracts opened or returned in the period',
    inService: 'Cars in service', rented: 'Cars rented now', ready: 'Cars ready to rent',
    maintenance: 'Cars in maintenance - their status on Ejar (Business Bay / Invygo workshop)', held: 'Cars held for bookings',
    reg: 'Registrations expired or expiring within 30 days',
};
const PAGE_OF = {
    open: ['Open the contracts page', '/contracts'], inService: ['Open the fleet page', '/fleet'],
    maintenance: ['Open the maintenance page', '/maintenance'], held: ['Open the Daily rental report', '/daily-booking'],
    reg: ['Open the registrations page', '/registration'],
};
const BRANCH_NAME = { T1: 'Terminal 1', T2: 'Terminal 2', BB: 'Business Bay', INV: 'Invygo' };
// the branch table's column -> the list behind it
const COLUMN_LIST = { open: 'open', opened: 'opened', returned: 'returned', overdue: 'overdue', fleet: 'inService',
    rented: 'rented', ready: 'ready', maintenance: 'maintenance', held: 'held' };
const COLUMN_WORDS = { open: 'open now', opened: 'opened', returned: 'returned', overdue: 'past return', fleet: 'fleet',
    rented: 'rented', ready: 'ready', maintenance: 'in maintenance', held: 'held for bookings' };

// THE Overview (owner, 1 Oct 2026: "عايز افرفيو احترافى شامل"): the Home page's first tab and the KPI Dashboard's
// Overview - one board, one answer from the engine (engine/api/overview.py), shown at once from the browser's last
// copy (useOverview). Contracts for the period, the fleet now, contracts per day, every branch side by side, what
// needs attention, the fines and the latest contracts.
const COLORS = { 'Business Bay': 'var(--accent, #5b21b6)', Airport: '#facc15', Invygo: '#0ea5e9', Other: '#9ca3af' };
const shortDay = (k) => new Date(`${k}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const BRANCH_LABEL = { All: 'All branches', Airport: 'Airport', T1: 'Terminal 1', T2: 'Terminal 2', BB: 'Business Bay', INV: 'Invygo' };
const TONES = {
    default: 'text-gray-900', good: 'text-green-700', warn: 'text-amber-700', bad: 'text-red-700', accent: 'text-purple-700',
};

function Sparkline({ values }) {
    if (!values || values.length < 2) return null;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const span = max - min || 1;
    const pts = values.map((v, i) => `${(i / (values.length - 1)) * 72},${22 - ((v - min) / span) * 20}`).join(' ');
    return (
        <svg width="72" height="24" viewBox="0 0 72 24" aria-hidden="true" className="shrink-0">
            <polyline points={pts} fill="none" stroke="var(--accent, #5b21b6)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

/** One figure: label, the number, a line under it; clickable when it opens its page or list. */
function Kpi({ icon, label, value, tone = 'default', spark, delta, meta, meta2, badge, onClick, title }) {
    const body = (
        <>
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 min-w-0">
                    <Icon glyph={icon} size="sm" tone="muted" /><span className="truncate">{label}</span>
                </span>
                {badge}
            </div>
            <div className="flex items-end justify-between gap-2">
                <span className={`text-3xl font-bold tabular-nums leading-tight ${TONES[tone] || TONES.default}`}>{value}</span>
                <Sparkline values={spark} />
            </div>
            <div className="text-xs text-gray-600 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-h-[1rem]">
                {delta}{meta && <span>{meta}</span>}
            </div>
            {meta2 && <div className="text-xs text-gray-700 font-medium">{meta2}</div>}
        </>
    );
    const box = 'rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-1 min-w-0 text-start';
    return onClick ? (
        <button type="button" onClick={onClick} title={title} className={`${box} hover:border-purple-400 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500`}>{body}</button>
    ) : <div className={box} title={title}>{body}</div>;
}

const Delta = ({ value, suffix = '' }) => (value == null ? null : (
    <span className={`font-semibold ${value >= 0 ? 'text-green-700' : 'text-red-700'}`}>{value >= 0 ? '↑' : '↓'} {Math.abs(value)}{suffix}</span>
));

const Section = ({ title, note, children }) => (
    <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
            {title}{note && <span className="ml-2 font-normal normal-case tracking-normal text-gray-500">{note}</span>}
        </h3>
        {children}
    </section>
);

/** Contracts opened per day: at least the last two weeks, the chosen period in colour, each bar's number on it. */
function OpenedChart({ chart, onDay }) {
    const totalOf = (g) => Object.values(g).reduce((a, b) => a + b, 0);
    const inPeriod = (day) => day >= (chart.periodFrom || chart.from) && day <= (chart.periodTo || chart.to);
    const chartMax = Math.max(1, ...chart.series.map(([, g]) => totalOf(g)));
    const labels = chart.series.length <= 31;
    const periodSum = (grp) => chart.series.filter(([d]) => inPeriod(d)).reduce((a, [, g]) => a + (g[grp] || 0), 0);
    const periodTotal = chart.groups.reduce((a, grp) => a + periodSum(grp), 0);
    const oneDay = chart.periodFrom === chart.periodTo;
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-3 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="font-semibold text-gray-900">Contracts opened per day</div>
                    <div className="text-xs text-gray-500">
                        {shortDay(chart.from)} to {shortDay(chart.to)} · {oneDay ? `${shortDay(chart.periodFrom)} in colour` : 'the chosen period in colour'}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-700">
                    {chart.groups.map((g) => (
                        <span key={g} className="inline-flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[g] }} />{g} <b className="tabular-nums">{periodSum(g)}</b>
                        </span>
                    ))}
                    <span className="font-semibold text-gray-900">Total {periodTotal}</span>
                </div>
            </div>
            <div className="relative h-[220px] flex items-end gap-1 border-b border-gray-200 pl-6 pt-4">
                {[0.25, 0.5, 0.75, 1].map((p) => (
                    <div key={p} className="absolute left-6 right-0 border-t border-dashed border-gray-100 pointer-events-none" style={{ bottom: `${p * 92}%` }}>
                        <span className="absolute -top-2 -left-6 w-5 text-right text-[10px] text-gray-400">{Math.round(chartMax * p)}</span>
                    </div>
                ))}
                {chart.series.map(([day, g]) => {
                    const total = totalOf(g);
                    const on = inPeriod(day);
                    return (
                        <button type="button" key={day} aria-label={`${day}: ${total} contracts`}
                            className={`relative flex-1 h-full flex flex-col-reverse group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-sm ${on ? '' : 'opacity-35'}`}
                            onClick={() => onDay(day)}>
                            {chart.groups.map((grp) => (g[grp] > 0 ? (
                                <span key={grp} className="block w-full" style={{ height: `${(g[grp] / chartMax) * 92}%`, backgroundColor: COLORS[grp] }} />
                            ) : null))}
                            {labels && total > 0 && (
                                <span className={`absolute left-1/2 -translate-x-1/2 text-[10px] tabular-nums ${on ? 'font-bold text-gray-900' : 'text-gray-500'}`}
                                    style={{ bottom: `calc(${(total / chartMax) * 92}% + 2px)` }}>{total}</span>
                            )}
                            <span className="hidden group-hover:flex group-focus-visible:flex flex-col items-center absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded-md z-20 whitespace-nowrap pointer-events-none text-[11px]">
                                <span>{shortDay(day)}: {total}</span>
                                <span className="text-gray-300">{chart.groups.filter((grp) => g[grp]).map((grp) => `${grp} ${g[grp]}`).join(' · ') || 'none'}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
            <div className="flex gap-1 text-[10px] text-gray-500 pl-6">
                {chart.series.map(([day], i, a) => (
                    <span key={day} className={`flex-1 text-center ${inPeriod(day) ? 'font-semibold text-gray-800' : ''}`}>
                        {i % Math.max(1, Math.ceil(a.length / 10)) === 0 || i === a.length - 1 || (oneDay && inPeriod(day)) ? shortDay(day) : ''}
                    </span>
                ))}
            </div>
        </div>
    );
}

/** Every branch side by side: contracts and the fleet now. */
function BranchTable({ data, filter, onOpen }) {
    const rows = branchRows(data, filter);
    const num = (k, tone) => ({
        key: k, accessor: (r) => r[k],
        // a number opens the records behind it: that branch's (the Total row: the filter's)
        cell: (r) => (r[k] ? (
            <button type="button" onClick={() => onOpen(COLUMN_LIST[k], r.code === 'TOTAL' ? null : r.code)}
                title={`Show the ${fmtN(r[k])}`} aria-label={`${r.branch} ${COLUMN_WORDS[k]}: ${fmtN(r[k])}`}
                className={`tabular-nums underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded ${r.code === 'TOTAL' ? 'font-bold' : ''} ${tone || ''}`}>
                {fmtN(r[k])}
            </button>
        ) : <span className={`tabular-nums ${r.code === 'TOTAL' ? 'font-bold' : ''}`}>{fmtN(r[k])}</span>),
    });
    const columns = [
        { key: 'branch', header: 'Branch', cell: (r) => <span className={r.code === 'TOTAL' ? 'font-bold' : 'font-medium'}>{r.branch}</span> },
        { ...num('open'), header: 'Open now' },
        { ...num('opened'), header: 'Opened' },
        { ...num('returned'), header: 'Returned' },
        { ...num('overdue', 'text-red-700 font-semibold'), header: 'Past return' },
        { ...num('fleet'), header: 'Fleet' },
        { ...num('rented'), header: 'Rented' },
        { ...num('ready', 'text-green-700 font-semibold'), header: 'Ready' },
        { ...num('maintenance', 'text-amber-700 font-semibold'), header: 'Maintenance' },
        { ...num('held', 'text-blue-700 font-semibold'), header: 'Held for bookings' },
    ];
    return (
        <DataTable title="By branch" description="Contracts in the period · the fleet now" columns={columns} data={rows}
            rowKey="code" pagination={false} searchable={false} emptyIcon={ICONS.fleet} emptyTitle="No branch in this filter." />
    );
}

/** The board's own "Needs attention" list (the KPI page); the Home page passes `attentionPanel` to merge its own. */
function AttentionList({ items, onAction }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">
                Needs attention <span className="ml-1 text-gray-500 font-normal">{items.length}</span>
            </div>
            {items.length === 0 ? (
                <div className="p-6 text-center text-sm text-green-700 font-medium">Nothing needs attention.</div>
            ) : items.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0">
                    <span className={a.tone === 'danger' ? 'text-red-600' : 'text-amber-600'}><Icon glyph={a.icon} size="sm" /></span>
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 truncate">{a.title}</span>
                        <span className="block text-xs text-gray-500 truncate">{a.sub}</span>
                    </span>
                    {a.action && <Button size="sm" variant="secondary" onClick={() => onAction(a.action)}>{a.action.label}</Button>}
                </div>
            ))}
        </div>
    );
}

/** The fines: company-wide - they do NOT follow the period and the branch, so the card says so (audit MON-21). */
function FinesCard({ fines, onOpen, onBox }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700"><Icon glyph={ICONS.fines} size="sm" tone="muted" />Unpaid fines</span>
                <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-gray-100 text-gray-800">{UNPAID_FINES_SCOPE}</span>
            </div>
            {fines ? (
                <>
                    <span className="text-3xl font-bold tabular-nums text-red-700">AED {fmtN(fines.unpaid.billed)}</span>
                    <div className="grid grid-cols-3 gap-2 text-center">
                        {[['Unpaid', fines.unpaid.count, FINES_LINK.unpaid], ['Not matched', fines.unmatched.count, FINES_LINK.unmatched],
                            ['To review', fines.review.count, FINES_LINK.review]].map(([k, v, to]) => (
                            <button type="button" key={k} onClick={() => onBox(to)} title={`Open the ${k.toLowerCase()} fines`}
                                className="rounded-lg bg-gray-50 px-2 py-2 hover:bg-purple-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500">
                                <span className="block text-[11px] text-gray-600">{k}</span>
                                <span className="block text-lg font-bold tabular-nums text-gray-900">{fmtN(v)}</span>
                            </button>
                        ))}
                    </div>
                    <Button size="sm" variant="secondary" onClick={onOpen}>Open the fines</Button>
                </>
            ) : <span className="text-sm text-gray-600">The fines could not be read.</span>}
        </div>
    );
}

const perBranch = (byBranch, key) => Object.fromEntries(Object.entries(byBranch || {}).map(([k, v]) => [k, v[key]]));

/**
 * The Overview for a period and a branch (`filters` = { from, to, branch }: All / Airport / T1 / T2 / BB / INV or the
 * KPI page's branch names) from `overview` = useOverview(filters). `attentionPanel(items, onAction, data)` replaces
 * the board's own "Needs attention" list (the Home page merges its engine checks and cases closed by hand).
 */
export function OverviewBoardView({ filters, overview, attentionPanel, showHeading = true }) {
    const navigate = useNavigate();
    const { data, error, loading, kept, refresh } = overview;
    // permissions phase 4 (4 Oct 2026): the Ejar sync's Retry and the Refresh that rebuilds the figures, each its own
    const { hasPermission } = usePermissions();
    const canSync = hasPermission('run_ejar_sync');
    const canRebuild = hasPermission('kpi_rebuild');
    const [modal, setModal] = useState({ open: false, title: '', data: [] });
    const [syncing, setSyncing] = useState(false);
    const [profileSeed, setProfileSeed] = useState(null);   // a recent contract clicked: its customer 360

    // the records behind a figure, for the filter's branch or one branch of the table (``code``); the newest click
    // wins - an older answer arriving late never fills the list open now
    const asked = useRef(0);
    const openList = async (what, code) => {
        const ticket = ++asked.current;
        const where = code ? BRANCH_NAME[code] || code : (filters.branch && filters.branch !== 'All' ? BRANCH_LABEL[filters.branch] || filters.branch : '');
        const title = `${LISTS[what]}${where ? ` · ${where}` : ''}`;
        const page = PAGE_OF[what];
        setModal({ open: true, title, data: [], loading: true, action: page ? { label: page[0], onClick: () => navigate(page[1]) } : null });
        try {
            const r = await finesService.overviewList({ what, from: filters.from, to: filters.to, branch: code || filters.branch || 'All' });
            if (ticket !== asked.current) return;
            setModal((m) => ({ ...m, data: r.items || [], loading: false,
                note: r.truncated ? `only the first ${(r.items || []).length} - pick a shorter period for the rest` : '' }));
        } catch (e) {
            if (ticket !== asked.current) return;
            setModal((m) => ({ ...m, loading: false }));
            toast.error(`Could not read the list: ${errorMessage(e)}`);
        }
    };
    const showOverdue = () => openList('overdue');
    const onAction = async (action) => {
        if (action.to) { navigate(action.to); return; }
        if (action.run === 'overdue') {
            showOverdue();
        } else if (action.run === 'sync') {
            setSyncing(true);
            try {
                await finesService.ejarSyncNow();
                toast.success('Ejar sync started.');
            } catch (e) {
                toast.error(`Could not start the sync: ${errorMessage(e)}`);
            } finally {
                setSyncing(false);
            }
        }
    };
    const openDay = async (day) => {
        const ticket = ++asked.current;
        setModal({ open: true, title: `Contracts opened on ${day}`, data: [], loading: true });
        try {
            const r = await finesService.overviewOpened({ day, branch: filters.branch });
            if (ticket !== asked.current) return;
            setModal({ open: true, title: `Contracts opened on ${day}`, data: r.items || [] });
        } catch (e) {
            if (ticket !== asked.current) return;
            setModal((m) => ({ ...m, loading: false }));
            toast.error(`Could not read that day: ${errorMessage(e)}`);
        }
    };

    if (!data) {
        return error ? (
            <div className="flex flex-col items-start gap-3">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
                    The Overview could not be read: {String(error).replace(/\.+$/, '')}. Nothing is shown rather than zeros.
                </div>
                <Button size="sm" onClick={refresh} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Try again</Button>
            </div>
        ) : <div className="text-sm text-gray-600 py-10 text-center" role="status">Loading the Overview…</div>;
    }

    const c = data.contracts;
    const f = data.fleet;
    const items = attentionOf(data).map((a) => (a.id === 'ejar' && syncing ? { ...a, action: { ...a.action, label: 'Starting…' } } : a));
    const branchLabel = BRANCH_LABEL[filters.branch] || filters.branch || 'All branches';
    const builtAt = data.builtAt ? dubaiTime(data.builtAt) : '';
    // the period's last moment: "30 Sept 23:59" (the engine's end is the next midnight)
    const endAt = c?.openAt ? new Date(new Date(c.openAt).getTime() - 60000) : null;
    const endLabel = endAt ? endAt.toLocaleString('en-GB', { timeZone: 'Asia/Dubai', hourCycle: 'h23', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
    const endIsPast = !!(c && data.builtAt && c.openAt && new Date(c.openAt).getTime() < new Date(data.builtAt).getTime() - 60000);
    const others = f ? Object.entries(f.otherStatuses || {}).map(([k, v]) => `${k} ${v}`).join(' · ') : '';
    const recentColumns = [
        { key: 'n', header: '#', accessor: (r) => (c ? c.recent.indexOf(r) + 1 : ''), sortable: false },
        { key: 'contractNo', header: 'Contract', cell: (r) => <CellId>{r.contractNo}</CellId> },
        { key: 'customer', header: 'Customer', cell: (r) => <CellText>{r.customer || '—'}</CellText> },
        {
            key: 'vehicle', header: 'Vehicle', accessor: (r) => `${r.model || ''} ${r.plateNo || ''}`,
            cell: (r) => (<span className="inline-flex items-center gap-2"><CellText>{r.model || '—'}</CellText><CellPlate>{r.plateNo}</CellPlate></span>),
        },
        { key: 'pickupAt', header: 'Pick-up', accessor: (r) => r.pickupDate, sortValue: (r) => r.pickupAt, cell: (r) => <span className="ds-cell-date">{r.pickupDate || '—'}</span> },
        { key: 'dropoffAt', header: 'Return', accessor: (r) => r.dropoffDate, sortValue: (r) => r.dropoffAt, cell: (r) => <span className="ds-cell-date">{r.dropoffDate || '—'}</span> },
        { key: 'status', header: 'Status', accessor: (r) => contractStatusOf(r).label, cell: (r) => <CellStatus status={contractStatusOf(r).label} label={contractStatusOf(r).label} tone={contractStatusOf(r).tone} /> },
        { key: 'branch', header: 'Branch', cell: (r) => <CellMuted>{r.branch}</CellMuted> },
    ];

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-end justify-between gap-2">
                {showHeading ? (
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Overview</h2>
                        <p className="text-sm text-gray-600">
                            {new Date(`${filters.to}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · {branchLabel}
                        </p>
                    </div>
                ) : <span />}
                <span className="flex items-center gap-2 text-xs text-gray-500" aria-live="polite">
                    {loading ? (kept ? 'Showing the last figures · updating…' : 'Updating…') : builtAt ? `Updated ${builtAt}` : ''}
                    {error && !loading && <span className="text-red-700">· could not update: {String(error).slice(0, 80)}</span>}
                    {canRebuild && <Button size="xs" variant="ghost" onClick={refresh} disabled={loading} leadingIcon={<Icon glyph={ICONS.refresh} size="xs" />}>Refresh</Button>}
                </span>
            </div>

            <Section title="Contracts" note={`${branchLabel} · as Ejar has them`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    {/* open NOW - Ejar's screen and the fleet's Rented say the same; the period's end is the line under it */}
                    <Kpi icon={ICONS.contracts} label="Open now" value={c ? fmtN(c.openNow ?? c.open) : '—'} spark={c?.spark}
                        delta={c ? <Delta value={c.change} suffix="%" /> : null}
                        meta={c ? (c.openNow != null && endIsPast ? `vs a week before · ${fmtN(c.open)} at the end of ${endLabel}` : 'vs a week before') : 'The contracts could not be read'}
                        meta2={c ? openByBranchText(c.openNowByBranch || c.openByBranch) : null}
                        onClick={c ? () => openList('open') : undefined} title="Show the contracts open now" />
                    <Kpi icon={ICONS.plus} label="Opened" value={c ? fmtN(c.openedTotal) : '—'} tone="accent"
                        meta="in the period" meta2={c ? openByBranchText(c.openedByBranch) || 'none' : null}
                        onClick={c ? () => openList('opened') : undefined} title="Show the contracts opened in the period" />
                    <Kpi icon={ICONS.check} label="Returned" value={c ? fmtN(c.returnedTotal) : '—'} tone="good"
                        meta="closed in the period" meta2={c ? openByBranchText(c.returnedByBranch) || 'none' : null}
                        onClick={c ? () => openList('returned') : undefined} title="Show the contracts returned in the period" />
                    <Kpi icon={ICONS.clock} label="Past return date" value={c ? fmtN(c.overdue.length) : '—'} tone={c && c.overdue.length ? 'bad' : 'good'}
                        meta={c && c.overdue.length ? `longest ${c.overdue[0].daysLate} days · open now` : 'open now'}
                        meta2={c ? openByBranchText(c.overdueByBranch) : null}
                        onClick={c ? showOverdue : undefined} title="Show the contracts past their return date" />
                    <Kpi icon={ICONS.kpi} label="Utilisation" value={c && c.util != null ? `${c.util}%` : '—'}
                        meta="open now ÷ fleet in service"
                        onClick={f ? () => openList('inService') : undefined} title="Show the fleet in service and what each car is doing" />
                </div>
            </Section>

            <Section title="Fleet" note="now · the car's status from Ejar">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
                    <Kpi icon={ICONS.fleet} label="In service" value={f ? fmtN(f.inService) : '—'}
                        meta={f ? 'registered with RTA, not sold' : 'The fleet could not be read'} meta2={others || null}
                        onClick={f ? () => openList('inService') : undefined} title="Show the cars in service" />
                    <Kpi icon={ICONS.car} label="Rented" value={f ? fmtN(f.rented) : '—'} tone="accent"
                        meta={f ? `on open contracts now · ${pct(f.rented, f.inService)}% of the fleet` : null}
                        meta2={f ? openByBranchText(perBranch(f.byBranch, 'rented')) : null}
                        onClick={f ? () => openList('rented') : undefined} title="Show the cars rented now" />
                    <Kpi icon={ICONS.check} label="Ready" value={f ? fmtN(f.ready) : '—'} tone="good"
                        meta="available to rent" meta2={f ? openByBranchText(perBranch(f.byBranch, 'ready')) || 'none' : null}
                        onClick={f ? () => openList('ready') : undefined} title="Show the cars ready to rent" />
                    {/* the cars whose status on Ejar says Maintenance (owner, 3 Oct 2026: the 22 - not the maintenance
                        records, which hold only the cars someone logged) */}
                    <Kpi icon={ICONS.maintenance} label="In maintenance" value={f ? fmtN(f.maintenance) : '—'} tone="warn"
                        meta="in the workshop · Business Bay / Invygo" meta2={f ? openByBranchText(perBranch(f.byBranch, 'maintenance')) || 'none' : null}
                        onClick={f ? () => openList('maintenance') : undefined} title="Show the cars in maintenance" />
                    {/* owner, 1 Oct 2026: a car in Maintenance status at T1 / T2 is kept for a booking (the Daily rental
                        report's "Held for bookings") - never the workshop; at Business Bay / Invygo it is real
                        maintenance (owner, 29 Oct 2026) */}
                    <Kpi icon={ICONS.booking} label="Held for bookings" value={f && f.held !== undefined ? fmtN(f.held) : '—'}
                        meta="Maintenance status at T1 / T2"
                        meta2={f ? openByBranchText(perBranch(f.byBranch, 'held')) || 'none' : null}
                        onClick={f ? () => openList('held') : undefined} title="Show the cars held for bookings" />
                    <Kpi icon={ICONS.calendar} label="Registrations expiring" value={f ? fmtN(f.regSoon.length) : '—'} tone={f && f.regExpired.length ? 'bad' : 'default'}
                        badge={<span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">30 days</span>}
                        meta={f ? `${f.regExpired.length} already expired` : null}
                        onClick={f ? () => openList('reg') : undefined} title="Show the registrations expired or expiring" />
                </div>
            </Section>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="xl:col-span-2 min-w-0">{c ? <OpenedChart chart={c.opened} onDay={openDay} /> : null}</div>
                <FinesCard fines={data.fines} onOpen={() => navigate('/traffic-fines')} onBox={(to) => navigate(to)} />
            </div>

            <BranchTable data={data} filter={filters.branch} onOpen={openList} />

            {attentionPanel ? attentionPanel(items, onAction, data)
                : <AttentionList items={canSync ? items : items.map((a) => (a.action?.run === 'sync' ? { ...a, action: null } : a))} onAction={onAction} />}

            <DataTable
                title="Recent contracts"
                description={`The last ${c ? c.recent.length : 0} contracts opened by the end of the period · ${branchLabel}`}
                headerActions={<Button size="sm" variant="secondary" onClick={() => openList('moved')}
                    title="Every contract opened or returned in the period">View all</Button>}
                columns={recentColumns}
                data={c ? c.recent : []}
                rowKey="contractNo"
                pagination={false}
                searchable={false}
                onRowClick={(r) => setProfileSeed({ contractNo: r.contractNo, name: r.customer || '', plate: r.plateNo || '' })}
                emptyIcon={ICONS.contracts}
                emptyTitle={c ? 'No contracts opened yet.' : 'The contracts could not be read.'}
            />

            <CustomerProfileModal open={!!profileSeed} seed={profileSeed} onClose={() => setProfileSeed(null)} onCopy={() => {}} />

            <DrillDownModal isOpen={modal.open} onClose={() => setModal({ ...modal, open: false })} title={modal.title} data={modal.data}
                loading={!!modal.loading} action={modal.action} note={modal.note} exportKey={null} />
        </div>
    );
}

/** The board with its own answer (the KPI Dashboard's Overview). */
export default function OverviewBoard(props) {
    const overview = useOverview(props.filters);
    return <OverviewBoardView {...props} overview={overview} />;
}
